import React, { useState, useEffect } from "react";
import swal from "sweetalert";
import {
  createTriggerService,
  updateTriggerService,
  getCanvasAssignmentsService,
} from "../../selectors/triggerService";

export const TriggerForm = ({ courseId, canvasId, trigger, onClose }) => {
  const [formData, setFormData] = useState({
    canvas_assignment_id: "",
    assignment_name: "",
    minimum_score: "",
    active: true,
  });
  const [assignments, setAssignments] = useState([]);
  const [loadingAssignments, setLoadingAssignments] = useState(false);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (canvasId) {
      setLoadingAssignments(true);
      getCanvasAssignmentsService(canvasId)
        .then((resp) => {
          if (resp.data.status || resp.data.success) {
            const data = resp.data.data || [];
            setAssignments(Array.isArray(data) ? data : []);
          }
        })
        .catch((error) => {
          console.error("Error al cargar actividades:", error);
          setAssignments([]);
        })
        .finally(() => {
          setLoadingAssignments(false);
        });
    }

    if (trigger) {
      setFormData({
        canvas_assignment_id: trigger.canvas_assignment_id || "",
        assignment_name: trigger.assignment_name || "",
        minimum_score: trigger.minimum_score || "",
        active: trigger.active || false,
      });
    }
  }, [canvasId, trigger]);

  const handleAssignmentChange = (e) => {
    const selectedId = e.target.value;
    const selected = assignments.find((a) => a.id == selectedId);
    setFormData({
      ...formData,
      canvas_assignment_id: selectedId,
      assignment_name: selected ? selected.name : "",
    });
  };

  const handleSubmit = (e) => {
    e.preventDefault();

    if (!formData.canvas_assignment_id || !formData.minimum_score) {
      swal("Error", "Debe seleccionar una actividad e ingresar la nota mínima", "error");
      return;
    }

    if (formData.minimum_score < 0 || formData.minimum_score > 5) {
      swal("Error", "La nota debe estar entre 0 y 5", "error");
      return;
    }

    setLoading(true);

    const payload = {
      course_id: courseId,
      canvas_assignment_id: formData.canvas_assignment_id,
      assignment_name: formData.assignment_name,
      minimum_score: parseFloat(formData.minimum_score),
      active: formData.active ? 1 : 0,
    };

    const service = trigger
      ? updateTriggerService(trigger.id, payload)
      : createTriggerService(payload);

    service
      .then((resp) => {
        if (resp.data.status || resp.data.success) {
          swal(
            trigger ? "Trigger actualizado" : "Trigger creado",
            "",
            "success"
          );
          onClose();
        } else {
          const errorMsg = resp.data.errors ? resp.data.errors[0] : (resp.data.message || "Error al guardar");
          swal("Error", errorMsg, "error");
        }
      })
      .catch((error) => {
        const errorMsg = error.response?.data?.errors?.[0] || "Ocurrió un error al guardar el trigger";
        swal("Error", errorMsg, "error");
      })
      .finally(() => {
        setLoading(false);
      });
  };

  return (
    <div className="modal_img show">
      <button className="btn dimmisingModal top-1/4" onClick={onClose}>
        <img src="/assets/images/close.svg" alt="Cerrar" />
      </button>

      <div className="mt-3 absolute w-full top-1/4">
        <div className="flex mt-3">
          <div className="w-3/4 m-auto px-5 py-5 rounded-2xl bg-[#67b5dd]">
            <h2 className="text-3xl Metropolis-Extra-Bold color-titulos mb-4">
              {trigger ? "Editar Trigger" : "Nuevo Trigger"}
            </h2>

            <form onSubmit={handleSubmit}>
              <div className="mb-3">
                <label className="block text-gray-700 Metropolis-Bold mb-2">
                  Actividad de Canvas *
                </label>
                <select
                  value={formData.canvas_assignment_id}
                  onChange={handleAssignmentChange}
                  className="Metropolis-Bold placeholder:text-slate-400 block bg-white w-full rounded-md py-2 px-3 shadow-sm focus:outline-none focus:border-sky-500 focus:ring-sky-500 focus:ring-1 sm:text-sm"
                  required
                  disabled={!canvasId || loadingAssignments || assignments.length === 0}
                >
                  <option value="">Seleccione una actividad</option>
                  {assignments.map((assignment) => (
                    <option key={assignment.id} value={assignment.id}>
                      {assignment.name}
                    </option>
                  ))}
                </select>
                {!canvasId ? (
                  <p className="text-red-600 text-sm mt-1">
                    Debe configurar el Canvas ID del curso primero
                  </p>
                ) : loadingAssignments ? (
                  <p className="text-blue-600 text-sm mt-1">
                    Cargando actividades...
                  </p>
                ) : assignments.length === 0 ? (
                  <p className="text-yellow-600 text-sm mt-1">
                    No hay actividades disponibles en Canvas para este curso.
                  </p>
                ) : null}
              </div>

              <div className="mb-3">
                <label className="block text-gray-700 Metropolis-Bold mb-2">
                  Nota Mínima (0-5) *
                </label>
                <input
                  type="number"
                  min="0"
                  max="5"
                  step="0.1"
                  value={formData.minimum_score || ""}
                  onChange={(e) =>
                    setFormData({ ...formData, minimum_score: e.target.value })
                  }
                  className="Metropolis-Bold placeholder:text-slate-400 block bg-white w-full rounded-md py-2 px-3 shadow-sm focus:outline-none focus:border-sky-500 focus:ring-sky-500 focus:ring-1 sm:text-sm"
                  placeholder="Ej: 3.5"
                  required
                />
              </div>

              <div className="mb-3">
                <label className="flex items-center">
                  <input
                    type="checkbox"
                    checked={formData.active}
                    onChange={(e) =>
                      setFormData({ ...formData, active: e.target.checked })
                    }
                    className="mr-2"
                  />
                  <span className="Metropolis-Bold">Activo</span>
                </label>
              </div>

              <div className="flex gap-3">
                <button
                  type="submit"
                  disabled={loading || !canvasId}
                  className="bg-[#93b33a] rounded px-6 py-2 text-base text-white Metropolis-Bold disabled:opacity-50"
                >
                  {loading ? "Guardando..." : "Guardar"}
                </button>
                <button
                  type="button"
                  onClick={onClose}
                  className="bg-gray-500 rounded px-6 py-2 text-base text-white Metropolis-Bold"
                >
                  Cancelar
                </button>
              </div>
            </form>
          </div>
        </div>
      </div>
    </div>
  );
};
